import { WorkspaceRole } from '../components/ui/workspace-settings/types';
import { AbortError, QuotaExceededError } from './custom-errors';
import { supabase } from './supabase';
import { ImprovePromptParams, ImprovePromptResult } from './types';

async function throwIfQuotaExceeded(response: Response): Promise<void> {
  if (response.status !== 429) return;
  const body = await response.json().catch(() => ({}));
  throw new QuotaExceededError(body.error);
}

const MAX_ATTEMPTS = 3;
const BASE_RETRY_DELAY_MS = 1000;

// Retry Gateway-level failures only.
// Deliberately excludes 500 — that is our own catch block returning a deterministic app error ("Agent Not Found")

const RETRYABLE_STATUSES = new Set([502, 503, 504]);

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

// A failed CORS preflight or a dropped connection rejects `fetch` with a
// TypeError and no Response attached, so there is no status code to match on.

function isNetworkError(error: unknown): boolean {
  return error instanceof TypeError;
}

// An LLM round-trip takes seconds, so a failure faster than this cannot have
// reached the provider — it died in the preflight or at the gateway, doing no
// server-side work. A slower failure may have completed server-side with only
// the response lost, and replaying that would bill a second provider call and
// deduct quota twice. Bounds the replay window until requests carry a real
// idempotency key.
const SAFE_REPLAY_WINDOW_MS = 2000;

// Exponential backoff, jittered
function backoffMs(attempt: number): number {
  return BASE_RETRY_DELAY_MS * 2 ** (attempt - 1) * (0.5 + Math.random());
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new AbortError());
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(new AbortError());
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

async function fetchWithRetry(
  url: string,
  init: RequestInit,
  signal?: AbortSignal,
): Promise<Response> {
  for (let attempt = 1; ; attempt++) {
    const isLastAttempt = attempt === MAX_ATTEMPTS;
    const startedAt = Date.now();
    // Too slow to be a gateway failure, so the request may already have spent
    // an LLM call server-side. Not safe to replay.
    const mayHaveRunServerSide = () =>
      Date.now() - startedAt > SAFE_REPLAY_WINDOW_MS;

    try {
      const response = await fetch(url, { ...init, signal });
      // Hand the final response back even when it is a 502 — the caller's own
      // error handling produces a better message than this loop can.
      if (
        isLastAttempt ||
        !RETRYABLE_STATUSES.has(response.status) ||
        mayHaveRunServerSide()
      ) {
        return response;
      }
    } catch (error) {
      if (
        isLastAttempt ||
        isAbortError(error) ||
        !isNetworkError(error) ||
        mayHaveRunServerSide()
      ) {
        throw error;
      }
    }

    await sleep(backoffMs(attempt), signal);
  }
}

interface EncryptApiKeyFunctionParams {
  // uuid
  apiKeyId?: string | null;

  // uuid
  agentId?: string | null;

  // plain text api key
  apiKey: string;

  workspaceId: string;
}

export async function encryptApiKey(params: EncryptApiKeyFunctionParams) {
  const { apiKeyId, agentId, apiKey, workspaceId } = params;

  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) throw new Error('User not authenticated');

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/encrypt-api-key`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'X-User-Token': session.access_token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          apiKeyId,
          agentId,
          apiKey,
          workspaceId,
        }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to add an API key');
    }

    return response.json();
  } catch (error) {
    console.error('Error while encrypting api key:', error);
    throw error;
  }
}

export async function getPersonaResponse(
  personaId: string,
  conversationHistory: Array<{
    role: 'user' | 'assistant';
    content: string;
  }> = [],
  workspaceId?: string,
  signal?: AbortSignal,
) {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) throw new Error('User not authenticated');

    const response = await fetchWithRetry(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/persona`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'X-User-Token': session.access_token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          personaId,
          conversationHistory,
          workspaceId,
        }),
      },
      signal,
    );

    await throwIfQuotaExceeded(response);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to get persona response');
    }

    const data = await response.json();
    return data.response;
  } catch (error: any) {
    if (error.name === 'AbortError') throw new AbortError();
    console.error('Error getting persona response:', error);
    throw error;
  }
}

export async function getChatResponse(
  agentId: string,
  message: string,
  conversationHistory: Array<{
    role: 'user' | 'assistant';
    content: string;
    metadata?: { threadId?: string };
  }>,
  workspaceId?: string,
  signal?: AbortSignal,
) {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) throw new Error('User not authenticated');

    const response = await fetchWithRetry(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'X-User-Token': session.access_token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          agentId: agentId || null,
          conversationHistory,
          message,
          workspaceId,
        }),
      },
      signal,
    );

    await throwIfQuotaExceeded(response);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to get chat response');
    }

    const data = await response.json();
    return data.response;
  } catch (error: any) {
    if (error.name === 'AbortError') throw new AbortError();
    console.error('Error getting chat response:', error);
    throw error;
  }
}

export async function improvePrompt(
  params: ImprovePromptParams,
  signal?: AbortSignal,
): Promise<ImprovePromptResult> {
  const { workspaceId, simulationId, evaluatorId, conversation, evaluation } =
    params;

  if (
    !workspaceId ||
    !simulationId ||
    !evaluatorId ||
    !Array.isArray(conversation) ||
    conversation.length === 0 ||
    !evaluation
  ) {
    throw new Error('Invalid parameters provided.');
  }

  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) throw new Error('User not authenticated');

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/prompt-improver`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'X-User-Token': session.access_token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          workspaceId,
          simulationId,
          evaluatorId,
          conversation,
          evaluation,
        }),
        signal,
      },
    );

    await throwIfQuotaExceeded(response);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to improve prompt');
    }

    return response.json();
  } catch (error) {
    console.error('Error improving prompt:', error);
    throw error;
  }
}

export async function evaluateDialogue(
  messages: Array<{ role: 'user' | 'assistant'; content: string }>,
  evaluationStatement: string,
  workspaceId?: string,
  /** Must match an `ai_models.name` (e.g. `gpt-4o-mini`, `grok-4`) so BYOK resolves to the right provider. */
  model?: string,
) {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) throw new Error('User not authenticated');

    const response = await fetchWithRetry(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/evaluate`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'X-User-Token': session.access_token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          dialogue: messages,
          statement: evaluationStatement,
          workspaceId,
          ...(model ? { model } : {}),
        }),
      },
    );

    await throwIfQuotaExceeded(response);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to get evaluation');
    }

    const result = await response.json();
    return { ...result, status: 'complete' };
  } catch (error) {
    console.error('Error evaluating dialogue:', error);
    throw error;
  }
}

export async function importPersonas(personasData: any, workspaceId?: string) {
  if (!personasData || !workspaceId)
    throw new Error('No personas data provided');

  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) throw new Error('User not authenticated');

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/import-personas`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'X-User-Token': session.access_token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: personasData,
          workspaceId,
        }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to import personas');
    }

    const result = await response.json();
    return { ...result, status: 'complete' };
  } catch (error) {
    console.error('Error importing personas:', error);
    throw error;
  }
}

export async function importAdversarialPersonas({
  workspaceId,
  key,
  statement,
  amount,
}: {
  workspaceId?: string;
  key: string;
  statement: string;
  amount: number;
}) {
  if (
    !workspaceId ||
    !key ||
    !statement ||
    !amount ||
    typeof amount !== 'number'
  )
    throw new Error('Invalid data provided.');

  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) throw new Error('User not authenticated');

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/import-adversarial-personas`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'X-User-Token': session.access_token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          workspaceId,
          key,
          statement,
          amount,
        }),
      },
    );

    await throwIfQuotaExceeded(response);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || 'Failed to import adversarial personas',
      );
    }

    const result = await response.json();
    return { ...result, status: 'complete' };
  } catch (error) {
    console.error('Error importing adversarial personas:', error);
    throw error;
  }
}

export async function inviteToWorkspace({
  workspaceId,
  invitedEmail,
  role,
}: {
  workspaceId?: string;
  invitedEmail: string;
  role: WorkspaceRole;
}) {
  if (!workspaceId || !invitedEmail || !role) return;

  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) throw new Error('User not authenticated');

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invite-to-workspace`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'X-User-Token': session.access_token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          workspace_id: workspaceId,
          invited_email: invitedEmail,
          invited_by: session.user.email,
          role,
        }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || 'Failed to invite a user to the workspace',
      );
    }

    const result = await response.json();
    return { ...result, status: 'complete' };
  } catch (error) {
    console.error('inviteToWorkspace function error:', error);
    throw error;
  }
}

export async function handleWorkspaceInvitation({
  invitationId,
  invitedByEmail,
  workspaceId,
}: {
  invitationId: string;
  invitedByEmail: string;
  workspaceId: string;
}) {
  if (!invitationId || !invitedByEmail || !workspaceId)
    return console.error('Invalid parameters provided.');

  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) throw new Error('User not authenticated');

    const response = await fetch(
      `${
        import.meta.env.VITE_SUPABASE_URL
      }/functions/v1/handle-workspace-invitation`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'X-User-Token': session.access_token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: invitationId,
          invited_email: session.user.email,
          invited_by: invitedByEmail,
          workspace_id: workspaceId,
        }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || 'Failed to handle workspace invitation',
      );
    }

    const result = await response.json();
    return { ...result, status: 'complete' };
  } catch (error) {
    console.error('handleWorkspaceInvitation function error:', error);
    throw error;
  }
}

export async function createNewWorkspace({ name }: { name: string }) {
  if (!name || name.trim() === '' || typeof name !== 'string') {
    throw new Error('Workspace name is required');
  }

  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) throw new Error('User not authenticated');

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-new-workspace`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'X-User-Token': session.access_token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
        }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to create a new workspace');
    }

    const result = await response.json();
    return { ...result, status: 'complete' };
  } catch (error) {
    console.error('createNewWorkspace function error:', error);
    throw error;
  }
}

export async function cloneWorkspace({
  name,
  workspaceId,
}: {
  name?: string;
  workspaceId?: string;
}) {
  if (
    !workspaceId ||
    workspaceId.trim() === '' ||
    typeof workspaceId !== 'string'
  ) {
    throw new Error('Workspace is invalid');
  }
  if (!name || name.trim() === '' || typeof name !== 'string') {
    throw new Error('Workspace name is required');
  }

  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) throw new Error('User not authenticated');

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/clone-workspace`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'X-User-Token': session.access_token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          workspaceId,
        }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to clone a workspace');
    }

    const result = await response.json();
    return { ...result, status: 'complete' };
  } catch (error) {
    console.error('cloneWorkspace function error:', error);
    throw error;
  }
}

export async function deleteWorkspace({
  workspaceId,
}: {
  workspaceId: string;
}) {
  if (!workspaceId || workspaceId.trim() === '') {
    throw new Error('Workspace ID is required');
  }

  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) throw new Error('User not authenticated');

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-workspace`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'X-User-Token': session.access_token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          workspaceId,
        }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to delete a workspace');
    }

    const result = await response.json();
    return { ...result, status: 'complete' };
  } catch (error) {
    console.error('deleteWorkspace function error:', error);
    throw error;
  }
}

export async function createCheckoutSession({
  workspaceId,
}: {
  workspaceId: string;
}) {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) throw new Error('User not authenticated');

    const { data, error } = await supabase.functions.invoke(
      'create-checkout-session',
      {
        body: { workspaceId },
        headers: {
          'X-User-Token': session.access_token,
        },
      },
    );

    if (error) {
      throw new Error('Failed to create checkout session');
    }

    return data;
  } catch (err) {
    console.error('createCheckoutSession error:', err);
    throw err;
  }
}

export async function leaveWorkspace({ workspaceId }: { workspaceId: string }) {
  if (!workspaceId || workspaceId.trim() === '') {
    throw new Error('Workspace ID is required');
  }

  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) throw new Error('User not authenticated');

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/leave-workspace`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'X-User-Token': session.access_token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          workspaceId,
        }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to leave the workspace');
    }

    const result = await response.json();
    return { ...result, status: 'complete' };
  } catch (error) {
    console.error('leaveWorkspace function error:', error);
    throw error;
  }
}

export async function populateWorkspaceFromTemplate(): Promise<{
  success: boolean;
  skipped?: boolean;
  reason?: string;
}> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) throw new Error('User not authenticated');

  const { data, error } = await supabase.functions.invoke(
    'populate-workspace-from-template',
    {
      headers: {
        'X-User-Token': session.access_token,
      },
    },
  );
  if (error) throw error;
  return data;
}
